import { createFileRoute, redirect, Outlet } from '@tanstack/react-router'
import { getLinkByCodeFn, recordClickFn } from '@/server/functions/links'
import { Loader2, ExternalLink, Link2 } from 'lucide-react'

export const Route = createFileRoute('/$code')({
  loader: async ({ params }) => {
    const result = await getLinkByCodeFn({
      data: {
        code: params.code,
      },
    })

    if (result.requiresPassword) {
      // Redirect to unlock page
      throw redirect({ href: `/${params.code}/unlock` })
    }

    if (!result.link) {
      // Link not found - return 404
      return { notFound: true, destinationUrl: null }
    }

    // Record the click
    await recordClickFn({
      data: {
        linkId: result.link.$id,
        shortCode: params.code,
      },
    })

    // Determine redirect URL
    let redirectUrl = result.link.originalUrl

    // Add UTM parameters if configured
    if (
      result.link.utmSource ||
      result.link.utmMedium ||
      result.link.utmCampaign
    ) {
      const url = new URL(redirectUrl)
      if (result.link.utmSource)
        url.searchParams.set('utm_source', result.link.utmSource)
      if (result.link.utmMedium)
        url.searchParams.set('utm_medium', result.link.utmMedium)
      if (result.link.utmCampaign)
        url.searchParams.set('utm_campaign', result.link.utmCampaign)
      if (result.link.utmTerm)
        url.searchParams.set('utm_term', result.link.utmTerm)
      if (result.link.utmContent)
        url.searchParams.set('utm_content', result.link.utmContent)
      redirectUrl = url.toString()
    }

    // Redirect to the destination
    throw redirect({ href: redirectUrl })
  },
  pendingComponent: RedirectingComponent,
  component: RedirectComponent,
})

function RedirectingComponent() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a0a0a] via-[#1A1F2C] to-[#0a0a0a] flex flex-col items-center justify-center p-6">
      {/* Background glow */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#9B87F5]/10 rounded-full blur-[150px]" />
      </div>

      <div className="relative z-10 text-center">
        {/* Logo */}
        <div className="flex items-center justify-center gap-3 mb-8">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#9B87F5] to-[#6E59A5] flex items-center justify-center shadow-lg shadow-[#9B87F5]/25">
            <Link2 className="w-6 h-6 text-white" />
          </div>
          <span className="text-2xl font-bold text-white">L</span>
        </div>

        {/* Loading animation */}
        <div className="flex items-center justify-center gap-3 mb-6">
          <Loader2 className="w-6 h-6 text-[#9B87F5] animate-spin" />
          <span className="text-white text-lg font-medium">
            Redirecting you...
          </span>
        </div>

        {/* Progress bar */}
        <div className="w-64 h-1.5 bg-[#1A1F2C] rounded-full overflow-hidden mb-8 mx-auto">
          <div
            className="h-full bg-gradient-to-r from-[#9B87F5] to-[#6E59A5] rounded-full animate-pulse"
            style={{ width: '60%' }}
          />
        </div>

        {/* Destination hint */}
        <div className="flex items-center justify-center gap-2 text-[#8E9196] text-sm mb-12">
          <ExternalLink className="w-4 h-4" />
          <span>Taking you to your destination</span>
        </div>
      </div>

      {/* Powered by footer */}
      <div className="absolute bottom-8 left-0 right-0 text-center">
        <a
          href="https://zugl.in"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex flex-col items-center gap-1 group"
        >
          <span className="text-[#8E9196] text-xs">Powered by</span>
          <span className="text-[#9B87F5] font-semibold text-sm group-hover:text-white transition-colors">
            zugl.in
          </span>
          <span className="text-[#8E9196]/60 text-[10px]">
            A Freelance Development Agency
          </span>
        </a>
      </div>
    </div>
  )
}

function RedirectComponent() {
  const data = Route.useLoaderData()

  if (data?.notFound) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0a0a0a] via-[#1A1F2C] to-[#0a0a0a] flex flex-col items-center justify-center p-6">
        {/* Background glow */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] bg-red-500/5 rounded-full blur-[100px]" />
        </div>

        <div className="relative z-10 text-center max-w-md">
          {/* Logo */}
          <div className="flex items-center justify-center gap-3 mb-8">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#9B87F5] to-[#6E59A5] flex items-center justify-center shadow-lg shadow-[#9B87F5]/25">
              <Link2 className="w-6 h-6 text-white" />
            </div>
            <span className="text-2xl font-bold text-white">L</span>
          </div>

          {/* 404 Message */}
          <div className="bg-[#1A1F2C]/50 backdrop-blur-xl border border-red-500/20 rounded-2xl p-8 mb-8">
            <div className="text-6xl font-bold text-red-400/80 mb-4">404</div>
            <h1 className="text-xl font-semibold text-white mb-2">
              Link Not Found
            </h1>
            <p className="text-[#8E9196] text-sm">
              The link you're looking for doesn't exist, has expired, or has
              been disabled by its owner.
            </p>
          </div>

          {/* Action */}
          <a
            href="/"
            className="inline-flex items-center gap-2 px-6 py-3 bg-[#9B87F5] hover:bg-[#8B5CF6] text-white font-medium rounded-xl transition-colors"
          >
            Create your own short link
          </a>
        </div>

        {/* Powered by footer */}
        <div className="absolute bottom-8 left-0 right-0 text-center">
          <a
            href="https://zugl.in"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex flex-col items-center gap-1 group"
          >
            <span className="text-[#8E9196] text-xs">Powered by</span>
            <span className="text-[#9B87F5] font-semibold text-sm group-hover:text-white transition-colors">
              zugl.in
            </span>
            <span className="text-[#8E9196]/60 text-[10px]">
              A Freelance Development Agency
            </span>
          </a>
        </div>
      </div>
    )
  }

  return <Outlet />
}
